import { computed, ref, type Ref } from "vue";
import { cloneDeep } from "lodash";
import type {
  Facet,
  SearchParams,
  SearchResponse,
  SearchResult,
} from "@/services/search";
import { search as searchSvc } from "@/services/search";
import type { SearchEndpoint } from "@/services/backend";
import { useUrlSearchParams } from "@vueuse/core";

export enum ResetType {
  None,
  Page,
  All,
}

export const rsSearchError = ref();

export default function useRsSearch<
  P extends SearchParams,
  E extends SearchResult,
  F extends Facet | undefined,
>(endpoint: SearchEndpoint, dParams: P, idsFieldName?: string) {
  const error: Ref<null | string> = ref(null);
  const searchResults: Ref<E[]> = ref([]);
  const isSearching = ref(false);
  const resultCount = ref(0);
  const pageSize = ref(0);
  const startAt = ref(0);
  const facets: Ref<F | undefined> = ref();
  const defaultParams = cloneDeep(dParams);
  const searchParams = ref(cloneDeep(dParams));
  const idsName = idsFieldName ?? "ids";
  let cache = new Map<number, E>();
  const urlParams = useUrlSearchParams("history");
  const doSetUrlParams = ref(false);

  async function search(resetType: ResetType) {
    isSearching.value = true;
    resetParams(resetType);

    try {
      const data = (await fetchApi(
        searchParams.value as Record<string, string>
      )) as SearchResponse<E, F>;
      searchResults.value = data.docs;
      resultCount.value = data.numFound;
      pageSize.value = data.maxRows;
      startAt.value = data.start;
      facets.value = data.facets;
      error.value = null;
    } catch (e: any) {
      error.value = `Error ${e.statusCode}: ${e.errorMsg}`;
      rsSearchError.value = error.value;
    }
    if (doSetUrlParams.value) {
      urlParams.p = getHasAllDefaults()
        ? []
        : JSON.stringify(getNonDefaultParams());
    }
    isSearching.value = false;
  }

  async function updateOffset(offset: number) {
    searchParams.value.start = offset;
    search(ResetType.None);
  }

  function resetParams(resetType: ResetType) {
    switch (resetType) {
      case ResetType.Page:
        searchParams.value.start = 0;
        break;
      case ResetType.All:
        searchParams.value = cloneDeep(defaultParams);
        searchParams.value.start = 0;
        break;
    }
  }

  function hasDefaultValue(paramName: string, mySearchParams?: any) {
    mySearchParams = mySearchParams ?? searchParams.value;
    return (
      JSON.stringify((mySearchParams as any)[paramName]) ===
      JSON.stringify((defaultParams as any)[paramName])
    );
  }

  function getNonDefaultParams() {
    let nonDefaultParams = cloneDeep(searchParams.value);
    Object.keys(nonDefaultParams).forEach((k) => {
      if (hasDefaultValue(k, nonDefaultParams)) {
        (nonDefaultParams as any)[k] = undefined;
      } else if ((searchParams.value as any)[k] === undefined) {
        (nonDefaultParams as any)[k] = "undefined";
      }
    });
    return nonDefaultParams;
  }

  function getEntriesById(params: P) {
    const ret = ref<Record<number, E>>({});
    fetchApi(params as any as Record<string, string>).then((res) => {
      ret.value = res.docs.reduce(
        (a: any, v: any) => ({ ...a, [v.id]: v }),
        {} as Record<number, E>
      );
    });
    return ret;
  }

  function getEntries(params: P) {
    const ret: Ref<E[]> = ref([]);
    fetchApi(params).then((res) => {
      ret.value = res.docs;
    });
    return ret;
  }

  function getSingleEntry(params: P) {
    params.maxRows = 1;
    const ret = ref<E>();
    fetchApi(params).then((res) => {
      ret.value = res.docs[0];
    });
    return ret;
  }

  function getEntriesByIdCached(ids: number[]) {
    const ret: Ref<E[]> = ref([]);
    let newIds: number[] = [];
    ids.forEach((id) => {
      if (cache.has(id)) {
        ret.value.push(cache.get(id) as E);
      } else {
        newIds.push(id);
      }
    });
    console.log(newIds);
    if (newIds.length) {
      const params = {
        [idsName]: JSON.stringify(newIds),
        maxRows: JSON.stringify(ids.length),
      };
      fetchApi(params).then((res) => {
        res.docs.forEach((e) => cache.set(Number(e.id), cloneDeep(e as E)));
        newIds.forEach((id) => ret.value.push(cache.get(id) as E));
      });
    }
    return ret;
  }

  async function fetchApi(searchParams: Record<string, string>) {
    return searchSvc(endpoint, searchParams);
  }

  function getHasAllDefaults() {
    return !Object.values(getNonDefaultParams()).some((e) => e != null);
  }

  return {
    searchParams,
    defaultParams,
    nonDefaultParams: computed(() => getNonDefaultParams()),
    hasDefaultValue,
    hasAllDefaults: computed(() => getHasAllDefaults()),
    searchResults: computed(() => searchResults.value),
    resultCount: computed(() => resultCount.value),
    facets: computed(() => facets.value),
    error: computed(() => error.value),
    isSearching: computed(() => isSearching.value),
    pageSize: computed(() => pageSize.value),
    startAt: computed(() => startAt.value),
    search,
    updateOffset,
    resetParams,
    getEntries,
    getEntriesById,
    getSingleEntry,
    getEntriesByIdCached,
    fetchApi,
    doSetUrlParams,
  };
}
